import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  User, Bell, Shield, Globe, Palette, Download, Trash2,
  Eye, EyeOff, Moon, Sun, Save, AlertTriangle, Loader,
} from 'lucide-react';
import toast from 'react-hot-toast';

const Toggle = ({ checked, onChange }) => (
  <label className="relative inline-flex items-center cursor-pointer">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="sr-only peer" />
    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer
      peer-checked:after:translate-x-full peer-checked:after:border-white
      after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all
      peer-checked:bg-primary" />
  </label>
);

const Settings = () => {
  const { user, updateProfile, changePassword, logout } = useAuth();
  const { theme, setLightTheme, setDarkTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Profil
  const [profile, setProfile] = useState({
    firstName: user?.firstName || '',
    lastName:  user?.lastName  || '',
    email:     user?.email     || '',
    phone:     user?.phone     || '',
    bio:       user?.bio       || '',
    location:  user?.location  || '',
  });

  // Notifications
  const [notifs, setNotifs] = useState({
    emailNotifications: true,
    pushNotifications:  true,
    courseUpdates:      true,
    marketingEmails:    false,
    weeklyDigest:       true,
  });

  // Préférences
  const [prefs, setPrefs] = useState({
    language: 'fr',
    timezone: 'Europe/Paris',
    fontSize: 'medium',
  });

  // Sécurité
  const [passwords, setPasswords] = useState({ current: '', newPwd: '', confirm: '' });
  const [twoFactor, setTwoFactor] = useState(false);
  const [pwdError, setPwdError] = useState('');

  // Confidentialité
  const [privacy, setPrivacy] = useState({
    profileVisibility: 'public',
    showEmail:         false,
    showPhone:         false,
    allowMessages:     true,
  });

  const handleSaveProfile = async () => {
    setSaving(true);
    const result = await updateProfile({
      firstName: profile.firstName,
      lastName:  profile.lastName,
      bio:       profile.bio,
      phone:     profile.phone,
      location:  profile.location,
    });
    setSaving(false);
    if (result.success) toast.success('Profil mis à jour');
    else toast.error(result.error || 'Erreur lors de la sauvegarde');
  };

  const handleSaveNotifs = async () => {
    setSaving(true);
    const result = await updateProfile({ preferences: { notifications: notifs, privacy, prefs } });
    setSaving(false);
    if (result.success) toast.success('Notifications sauvegardées');
    else toast.error(result.error || 'Erreur lors de la sauvegarde');
  };

  const handleSavePrefs = async () => {
    // Appliquer le thème immédiatement
    if (prefs.themeSelected === 'dark') setDarkTheme();
    else setLightTheme();
    setSaving(true);
    const result = await updateProfile({ preferences: { notifications: notifs, privacy, prefs } });
    setSaving(false);
    if (result.success) toast.success('Préférences sauvegardées');
    else toast.error(result.error || 'Erreur lors de la sauvegarde');
  };

  const handleSavePrivacy = async () => {
    setSaving(true);
    const result = await updateProfile({ preferences: { notifications: notifs, privacy, prefs } });
    setSaving(false);
    if (result.success) toast.success('Confidentialité sauvegardée');
    else toast.error(result.error || 'Erreur lors de la sauvegarde');
  };

  const handleChangePassword = async () => {
    setPwdError('');
    if (passwords.newPwd !== passwords.confirm) {
      setPwdError('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwords.newPwd.length < 6) {
      setPwdError('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    setSaving(true);
    const result = await changePassword(passwords.current, passwords.newPwd);
    setSaving(false);
    if (result.success) {
      toast.success('Mot de passe modifié');
      setPasswords({ current: '', newPwd: '', confirm: '' });
    } else {
      toast.error(result.error || 'Erreur changement de mot de passe');
    }
  };

  const [exporting, setExporting]         = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting]           = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await axios.get('/api/me/export', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `innovactive360-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export RGPD téléchargé');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'SUPPRIMER MON COMPTE') {
      toast.error('Tapez exactement : SUPPRIMER MON COMPTE');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete('/api/me/account', { data: { confirmation: 'SUPPRIMER MON COMPTE' } });
      toast.success('Compte supprimé. Au revoir.');
      await logout();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur suppression de compte');
      setDeleting(false);
    }
  };

  const tabs = [
    { id: 'profile',       label: 'Profil',          icon: User     },
    { id: 'notifications', label: 'Notifications',    icon: Bell     },
    { id: 'preferences',   label: 'Préférences',      icon: Palette  },
    { id: 'security',      label: 'Sécurité',         icon: Shield   },
    { id: 'privacy',       label: 'Confidentialité',  icon: Globe    },
    { id: 'data',          label: 'Données',          icon: Download },
  ];

  const SaveButton = ({ onClick, label = 'Sauvegarder' }) => (
    <button
      onClick={onClick}
      disabled={saving}
      className="flex items-center gap-2 bg-primary text-white px-5 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm font-medium"
    >
      <Save className="w-4 h-4" />
      {saving ? 'Sauvegarde…' : label}
    </button>
  );

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text mb-2">Paramètres</h1>
          <p className="text-gray-600">Gérez vos préférences et paramètres de compte</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <nav className="space-y-2">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm p-8">

              {/* ── Profil ────────────────────────────────────────── */}
              {activeTab === 'profile' && (
                <div>
                  <h2 className="text-2xl font-bold text-text mb-6 flex items-center gap-2">
                    <User className="w-6 h-6" /> Informations du profil
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    {[
                      { key: 'firstName', label: 'Prénom', type: 'text' },
                      { key: 'lastName',  label: 'Nom',    type: 'text' },
                      { key: 'phone',     label: 'Téléphone', type: 'tel' },
                      { key: 'location',  label: 'Localisation', type: 'text' },
                    ].map(f => (
                      <div key={f.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                        <input
                          type={f.type}
                          value={profile[f.key]}
                          onChange={e => setProfile(p => ({ ...p, [f.key]: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email" value={profile.email} disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">L'email ne peut pas être modifié</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                      <textarea
                        rows={4} value={profile.bio}
                        onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="mt-6">
                    <SaveButton onClick={handleSaveProfile} />
                  </div>
                </div>
              )}

              {/* ── Notifications ──────────────────────────────────── */}
              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-2xl font-bold text-text mb-6 flex items-center gap-2">
                    <Bell className="w-6 h-6" /> Notifications
                  </h2>
                  <div className="space-y-4">
                    {[
                      { key: 'emailNotifications', label: 'Notifications email',         desc: 'Recevoir des notifications par email' },
                      { key: 'pushNotifications',  label: 'Notifications push',           desc: 'Recevoir des notifications push' },
                      { key: 'courseUpdates',      label: 'Mises à jour de cours',        desc: 'Être notifié des nouvelles leçons' },
                      { key: 'marketingEmails',    label: 'Emails marketing',             desc: 'Recevoir des offres et promotions' },
                      { key: 'weeklyDigest',       label: 'Résumé hebdomadaire',          desc: 'Recevoir un résumé de votre semaine' },
                    ].map(opt => (
                      <div key={opt.key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <p className="font-semibold text-text text-sm">{opt.label}</p>
                          <p className="text-xs text-gray-500">{opt.desc}</p>
                        </div>
                        <Toggle checked={notifs[opt.key]} onChange={v => setNotifs(n => ({ ...n, [opt.key]: v }))} />
                      </div>
                    ))}
                  </div>
                  <div className="mt-6">
                    <SaveButton onClick={handleSaveNotifs} />
                  </div>
                </div>
              )}

              {/* ── Préférences ──────────────────────────────────────── */}
              {activeTab === 'preferences' && (
                <div>
                  <h2 className="text-2xl font-bold text-text mb-6 flex items-center gap-2">
                    <Palette className="w-6 h-6" /> Préférences
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Langue</label>
                      <select
                        value={prefs.language}
                        onChange={e => setPrefs(p => ({ ...p, language: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="fr">Français</option>
                        <option value="en">English</option>
                        <option value="es">Español</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fuseau horaire</label>
                      <select
                        value={prefs.timezone}
                        onChange={e => setPrefs(p => ({ ...p, timezone: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="Africa/Niamey">Africa/Niamey (UTC+1)</option>
                        <option value="Europe/Paris">Europe/Paris (UTC+1)</option>
                        <option value="Europe/London">Europe/London (UTC+0)</option>
                        <option value="America/New_York">America/New_York (UTC-5)</option>
                        <option value="Asia/Tokyo">Asia/Tokyo (UTC+9)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Thème</label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio" name="theme" value="light"
                            checked={theme === 'light'}
                            onChange={() => { setLightTheme(); setPrefs(p => ({ ...p, themeSelected: 'light' })); }}
                            className="w-4 h-4 text-primary"
                          />
                          <Sun className="w-5 h-5" /> Clair
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio" name="theme" value="dark"
                            checked={theme === 'dark'}
                            onChange={() => { setDarkTheme(); setPrefs(p => ({ ...p, themeSelected: 'dark' })); }}
                            className="w-4 h-4 text-primary"
                          />
                          <Moon className="w-5 h-5" /> Sombre
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Taille de police</label>
                      <select
                        value={prefs.fontSize}
                        onChange={e => setPrefs(p => ({ ...p, fontSize: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="small">Petite</option>
                        <option value="medium">Moyenne</option>
                        <option value="large">Grande</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-6">
                    <SaveButton onClick={handleSavePrefs} />
                  </div>
                </div>
              )}

              {/* ── Sécurité ──────────────────────────────────────────── */}
              {activeTab === 'security' && (
                <div>
                  <h2 className="text-2xl font-bold text-text mb-6 flex items-center gap-2">
                    <Shield className="w-6 h-6" /> Sécurité
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-text mb-4">Changer le mot de passe</h3>
                      <div className="space-y-4">
                        {[
                          { key: 'current', label: 'Mot de passe actuel',         show: showCurrentPassword, toggle: () => setShowCurrentPassword(v => !v) },
                          { key: 'newPwd',  label: 'Nouveau mot de passe',        show: showNewPassword,     toggle: () => setShowNewPassword(v => !v) },
                          { key: 'confirm', label: 'Confirmer le nouveau mot de passe', show: showConfirmPassword, toggle: () => setShowConfirmPassword(v => !v) },
                        ].map(f => (
                          <div key={f.key}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                            <div className="relative">
                              <input
                                type={f.show ? 'text' : 'password'}
                                value={passwords[f.key]}
                                onChange={e => setPasswords(p => ({ ...p, [f.key]: e.target.value }))}
                                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                              />
                              <button type="button" onClick={f.toggle}
                                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                                {f.show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                              </button>
                            </div>
                          </div>
                        ))}
                        {pwdError && <p className="text-red-500 text-sm">{pwdError}</p>}
                        <SaveButton onClick={handleChangePassword} label="Changer le mot de passe" />
                      </div>
                    </div>
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold text-text mb-4">Authentification à deux facteurs</h3>
                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <p className="font-semibold text-text text-sm">2FA activé</p>
                          <p className="text-xs text-gray-500">Sécurisez votre compte avec un code SMS</p>
                        </div>
                        <Toggle checked={twoFactor} onChange={setTwoFactor} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Confidentialité ──────────────────────────────────── */}
              {activeTab === 'privacy' && (
                <div>
                  <h2 className="text-2xl font-bold text-text mb-6 flex items-center gap-2">
                    <Globe className="w-6 h-6" /> Confidentialité
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Visibilité du profil</label>
                      <select
                        value={privacy.profileVisibility}
                        onChange={e => setPrivacy(p => ({ ...p, profileVisibility: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="public">Public</option>
                        <option value="private">Privé</option>
                      </select>
                    </div>
                    {[
                      { key: 'showEmail',     label: 'Afficher l\'email',     desc: 'Permettre aux autres de voir votre email' },
                      { key: 'showPhone',     label: 'Afficher le téléphone', desc: 'Permettre aux autres de voir votre téléphone' },
                      { key: 'allowMessages', label: 'Autoriser les messages', desc: 'Permettre aux autres de vous envoyer des messages' },
                    ].map(opt => (
                      <div key={opt.key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <p className="font-semibold text-text text-sm">{opt.label}</p>
                          <p className="text-xs text-gray-500">{opt.desc}</p>
                        </div>
                        <Toggle checked={privacy[opt.key]} onChange={v => setPrivacy(p => ({ ...p, [opt.key]: v }))} />
                      </div>
                    ))}
                  </div>
                  <div className="mt-6">
                    <SaveButton onClick={handleSavePrivacy} />
                  </div>
                </div>
              )}

              {/* ── Données ──────────────────────────────────────────── */}
              {activeTab === 'data' && (
                <div>
                  <h2 className="text-2xl font-bold text-text mb-6 flex items-center gap-2">
                    <Download className="w-6 h-6" /> Gestion des données
                  </h2>
                  <div className="space-y-6">
                    {/* Export RGPD */}
                    <div className="p-6 border border-gray-200 rounded-lg">
                      <h3 className="text-lg font-semibold text-text mb-2">Exporter mes données (Art. 15 RGPD)</h3>
                      <p className="text-gray-600 text-sm mb-4">
                        Téléchargez une copie complète de vos données personnelles : profil, inscriptions, progression, avis et notifications — au format JSON.
                      </p>
                      <button
                        onClick={handleExportData}
                        disabled={exporting}
                        className="flex items-center gap-2 bg-primary text-white px-5 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm font-medium"
                      >
                        {exporting ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {exporting ? 'Préparation…' : 'Télécharger mes données'}
                      </button>
                    </div>

                    {/* Suppression de compte */}
                    <div className="p-6 border border-red-200 rounded-lg bg-red-50">
                      <h3 className="text-lg font-semibold text-red-800 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" /> Zone de danger
                      </h3>
                      <p className="text-red-600 text-sm mb-4">
                        La suppression est irréversible et efface toutes vos données, inscriptions, progression et certificats (Art. 17 RGPD).
                      </p>
                      {!showDeleteModal ? (
                        <button
                          onClick={() => setShowDeleteModal(true)}
                          className="flex items-center gap-2 bg-red-600 text-white px-5 py-2 rounded-lg hover:bg-red-700 text-sm font-medium"
                        >
                          <Trash2 className="w-4 h-4" /> Supprimer mon compte
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm font-medium text-red-800">
                            Tapez <strong>SUPPRIMER MON COMPTE</strong> pour confirmer :
                          </p>
                          <input
                            type="text"
                            value={deleteConfirm}
                            onChange={e => setDeleteConfirm(e.target.value)}
                            placeholder="SUPPRIMER MON COMPTE"
                            className="w-full px-4 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setShowDeleteModal(false); setDeleteConfirm(''); }}
                              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                              Annuler
                            </button>
                            <button
                              onClick={handleDeleteAccount}
                              disabled={deleting || deleteConfirm !== 'SUPPRIMER MON COMPTE'}
                              className="flex items-center gap-2 bg-red-600 text-white px-5 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
                            >
                              {deleting ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                              {deleting ? 'Suppression…' : 'Confirmer la suppression'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
